/**
 * Sprint V101.d · Admin-Home (Sa 20.09.2026, Andreas).
 *
 * Overview-Landeseite unter /admin. Vier Tool-Karten:
 *   Users · Webinar · Feedback · Landing-Analytics
 *
 * KEIN Live-KPI-Dashboard hier — das kommt in Stufe 3.
 * Diese Seite ist bewusst schlank: Sie ist der Ankerpunkt der Sidebar-Nav
 * und muss beim Klick auf „Admin" in der Kopfzeile sofort verständlich sein.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Calendar,
  MessageSquare,
  BarChart3,
  Megaphone,
  ArrowUpRight,
} from "lucide-react";
import AdminLayout from "@/components/AdminLayout";

const TOOLS = [
  {
    to: "/admin/users",
    Icon: Users,
    title: "User-Verwaltung",
    desc: "Testuser anlegen, Admin-Bootstrap, Rollen prüfen.",
    testId: "admin-tool-users",
  },
  {
    to: "/admin/webinar",
    Icon: Calendar,
    title: "Webinar",
    desc: "Termin, Zoom-URL, Status und Banner ein/aus.",
    testId: "admin-tool-webinar",
  },
  {
    to: "/admin/marketing",
    Icon: Megaphone,
    title: "Marketing",
    desc: "Banner & In-App-Kampagnen (Trial-Push u.a.).",
    testId: "admin-tool-marketing",
  },
  {
    to: "/admin/feedback",
    Icon: MessageSquare,
    title: "Feedback & Signals",
    desc: "Kritiker-Feedback, Pionier-Reaktionen, Upgrade-Waitlist.",
    testId: "admin-tool-feedback",
  },
  {
    to: "/admin/kt400",
    Icon: Megaphone,
    title: "KT400-Seiten",
    desc: "Offer-URL und Button-Texte der Segmentierungsseiten.",
    testId: "admin-tool-kt400",
  },
  {
    to: "/admin/landing-analytics",
    Icon: BarChart3,
    title: "Landing-Analytics",
    desc: "Trichter, Klick-Gewinner, Regpage-Conversion.",
    testId: "admin-tool-analytics",
  },
];

export default function AdminHome() {
  return (
    <AdminLayout>
      <header className="mb-8">
        <p className="text-xs font-bold uppercase tracking-widest text-red-600">
          Admin
        </p>
        <h1
          className="mt-1 text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white"
          data-testid="admin-home-headline"
        >
          Deine Werkstatt hinter der Bühne.
        </h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
          Alle Tools an einem Ort — alles, was du zum Betrieb der Plattform brauchst. Der Admin-Token unten in der Sidebar gilt für alle Panels.
        </p>
      </header>

      <section
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        data-testid="admin-tool-grid"
      >
        {TOOLS.map(({ to, Icon, title, desc, testId }) => (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            className="group relative rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 sm:p-6 hover:border-[#F3651D] hover:shadow-lg transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#F3651D]/10 border border-[#F3651D]/30 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#F3651D]" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
                  {title}
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {desc}
                </p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-[#F3651D] transition-colors flex-shrink-0" />
            </div>
          </Link>
        ))}
      </section>

      <footer className="mt-10 text-xs text-slate-400 leading-relaxed">
        Alles read/write-fähig. Änderungen wirken sofort. Änderungen sind protokolliert (Backend-Logs).
      </footer>
    </AdminLayout>
  );
}
